/**
 * 이메일 유효성 검증 유틸리티 (다국어 지원)
 * @locale 패키지의 locale 파일을 사용합니다.
 */

/**
 * 이메일 유효성 검증 정규식
 * RFC 5322 표준을 기반으로 한 개선된 정규식
 */
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * 간단한 이메일 형식 검증 (기본적인 @ 포함 검증)
 */
const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 이메일 검증 에러 코드
 */
enum EmailValidationErrorCode {
  EMAIL_REQUIRED = 'EMAIL_REQUIRED',
  EMAIL_EMPTY = 'EMAIL_EMPTY',
  EMAIL_TOO_LONG = 'EMAIL_TOO_LONG',
  EMAIL_INVALID_FORMAT = 'EMAIL_INVALID_FORMAT',
  EMAIL_INVALID_STRICT = 'EMAIL_INVALID_STRICT',
  EMAIL_DOMAIN_TOO_LONG = 'EMAIL_DOMAIN_TOO_LONG',
  EMAIL_CONSECUTIVE_DOTS = 'EMAIL_CONSECUTIVE_DOTS',
  EMAIL_STARTS_OR_ENDS_WITH_DOT = 'EMAIL_STARTS_OR_ENDS_WITH_DOT',
  EMAIL_DOMAIN_INVALID = 'EMAIL_DOMAIN_INVALID',
}

/**
 * 에러 코드를 locale 키로 매핑
 */
const EMAIL_ERROR_CODE_TO_LOCALE_KEY = {
  [EmailValidationErrorCode.EMAIL_REQUIRED]: 'email_validation.email_required',
  [EmailValidationErrorCode.EMAIL_EMPTY]: 'email_validation.email_empty',
  [EmailValidationErrorCode.EMAIL_TOO_LONG]: 'email_validation.email_too_long',
  [EmailValidationErrorCode.EMAIL_INVALID_FORMAT]: 'email_validation.email_invalid_format',
  [EmailValidationErrorCode.EMAIL_INVALID_STRICT]: 'email_validation.email_invalid_strict',
  [EmailValidationErrorCode.EMAIL_DOMAIN_TOO_LONG]: 'email_validation.email_domain_too_long',
  [EmailValidationErrorCode.EMAIL_CONSECUTIVE_DOTS]: 'email_validation.email_consecutive_dots',
  [EmailValidationErrorCode.EMAIL_STARTS_OR_ENDS_WITH_DOT]:
    'email_validation.email_starts_or_ends_with_dot',
  [EmailValidationErrorCode.EMAIL_DOMAIN_INVALID]: 'email_validation.email_domain_invalid',
} as const;

/**
 * 지원되는 언어 타입
 */
export type SupportedLanguage = 'ko' | 'en' | 'ja';

/**
 * 이메일 유효성 검증 결과
 */
interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  errorCode?: EmailValidationErrorCode;
  normalizedEmail?: string;
}

/**
 * 메시지 가져오기 헬퍼 함수
 * @param errorCode 에러 코드
 * @param language 언어 (기본값: 'ko')
 * @returns 다국어 메시지
 */
function getEmailValidationMessage(
  errorCode: EmailValidationErrorCode | 'EMAIL_VALID' | 'EMAIL_HINT',
  language: SupportedLanguage = 'ko'
): string {
  // locale 파일의 키 매핑
  const localeKey =
    EMAIL_ERROR_CODE_TO_LOCALE_KEY[errorCode as EmailValidationErrorCode] ||
    (errorCode === 'EMAIL_VALID' ? 'email_validation.email_valid' : 'email_validation.email_hint');

  // i18n 라이브러리를 사용하여 메시지 가져오기
  // 실제 사용 시에는 i18n.t() 함수를 사용해야 합니다
  try {
    // i18n이 초기화된 경우 사용
    if (
      typeof window !== 'undefined' &&
      (window as unknown as { i18n: { t: (key: string, options: { lng: string }) => string } }).i18n
    ) {
      return (
        window as unknown as { i18n: { t: (key: string, options: { lng: string }) => string } }
      ).i18n.t(localeKey, { lng: language });
    }
  } catch (_error) {
    console.warn('i18n not available, using fallback messages', _error);
  }

  // i18n이 사용 불가능한 경우 fallback 메시지 사용
  const fallbackMessages = {
    ko: {
      'email_validation.email_required': '이메일 주소가 필요합니다.',
      'email_validation.email_empty': '이메일 주소가 비어있습니다.',
      'email_validation.email_too_long': '이메일 주소가 너무 깁니다. (최대 254자)',
      'email_validation.email_invalid_format': '올바른 이메일 형식이 아닙니다.',
      'email_validation.email_invalid_strict': '유효하지 않은 이메일 형식입니다.',
      'email_validation.email_domain_too_long': '도메인 부분이 너무 깁니다.',
      'email_validation.email_consecutive_dots': '이메일 주소에 연속된 점이 포함될 수 없습니다.',
      'email_validation.email_starts_or_ends_with_dot':
        '이메일 주소는 점으로 시작하거나 끝날 수 없습니다.',
      'email_validation.email_domain_invalid': '유효하지 않은 도메인 형식입니다.',
      'email_validation.email_valid': '올바른 이메일 형식입니다.',
      'email_validation.email_hint': '올바른 이메일 형식을 입력해주세요.',
    },
    en: {
      'email_validation.email_required': 'Email address is required.',
      'email_validation.email_empty': 'Email address is empty.',
      'email_validation.email_too_long': 'Email address is too long. (Maximum 254 characters)',
      'email_validation.email_invalid_format': 'Invalid email format.',
      'email_validation.email_invalid_strict': 'Invalid email format.',
      'email_validation.email_domain_too_long': 'Domain part is too long.',
      'email_validation.email_consecutive_dots': 'Email address cannot contain consecutive dots.',
      'email_validation.email_starts_or_ends_with_dot':
        'Email address cannot start or end with a dot.',
      'email_validation.email_domain_invalid': 'Invalid domain format.',
      'email_validation.email_valid': 'Valid email format.',
      'email_validation.email_hint': 'Please enter a valid email format.',
    },
  };

  return (
    fallbackMessages[language as keyof typeof fallbackMessages]?.[localeKey] ||
    fallbackMessages.ko[localeKey] ||
    'Invalid email format.'
  );
}

/**
 * 이메일 유효성 검증
 * @param email 검증할 이메일 주소
 * @param strict 엄격한 검증 여부 (기본값: true)
 * @param language 언어 설정 (기본값: 'ko')
 * @returns EmailValidationResult
 */
export function validateEmail(
  email: string,
  strict: boolean = true,
  language: SupportedLanguage = 'ko'
): EmailValidationResult {
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_REQUIRED, language),
      errorCode: EmailValidationErrorCode.EMAIL_REQUIRED,
    };
  }

  // 공백 제거 및 소문자 변환
  const normalizedEmail = email.trim().toLowerCase();

  // 길이 검증
  if (normalizedEmail.length === 0) {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_EMPTY, language),
      errorCode: EmailValidationErrorCode.EMAIL_EMPTY,
    };
  }

  if (normalizedEmail.length > 254) {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_TOO_LONG, language),
      errorCode: EmailValidationErrorCode.EMAIL_TOO_LONG,
    };
  }

  // 기본 형식 검증
  if (!SIMPLE_EMAIL_REGEX.test(normalizedEmail)) {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_INVALID_FORMAT, language),
      errorCode: EmailValidationErrorCode.EMAIL_INVALID_FORMAT,
    };
  }

  // 엄격한 검증 (strict 모드)
  if (strict && !EMAIL_REGEX.test(normalizedEmail)) {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_INVALID_STRICT, language),
      errorCode: EmailValidationErrorCode.EMAIL_INVALID_STRICT,
    };
  }

  // 도메인 부분 검증
  const [, domain] = normalizedEmail.split('@');
  if (domain.length > 253) {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_DOMAIN_TOO_LONG, language),
      errorCode: EmailValidationErrorCode.EMAIL_DOMAIN_TOO_LONG,
    };
  }

  // 연속된 점 검증
  if (normalizedEmail.includes('..')) {
    return {
      isValid: false,
      error: getEmailValidationMessage(EmailValidationErrorCode.EMAIL_CONSECUTIVE_DOTS, language),
      errorCode: EmailValidationErrorCode.EMAIL_CONSECUTIVE_DOTS,
    };
  }

  // 시작/끝 점 검증
  if (normalizedEmail.startsWith('.') || normalizedEmail.endsWith('.')) {
    return {
      isValid: false,
      error: getEmailValidationMessage(
        EmailValidationErrorCode.EMAIL_STARTS_OR_ENDS_WITH_DOT,
        language
      ),
      errorCode: EmailValidationErrorCode.EMAIL_STARTS_OR_ENDS_WITH_DOT,
    };
  }

  return {
    isValid: true,
    normalizedEmail,
  };
}
